import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { FileUpload } from '@/components/ui/file-upload'
import { Toast } from '@/components/ui/toast'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import {
  X,
  Upload,
  DollarSign,
  Tag,
  Truck,
  Clock,
  MapPin,
  AlertCircle,
  Image as ImageIcon,
  Info,
  Globe
} from 'lucide-react'

interface ProductFormProps {
  onClose: () => void
  onSuccess: () => void
  product?: any // Add product prop for editing
}

const CATEGORIES = [
  'Electronics',
  'Fashion',
  'Home & Garden',
  'Sports',
  'Beauty',
  'Books',
  'Toys',
  'Other'
]

// Shoe sizes
const SHOE_SIZES = ['35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47', '48'];

// Clothing sizes
const CLOTHING_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

// Common colors
const COMMON_COLORS = [
  { name: 'Noir', value: '#000000' },
  { name: 'Blanc', value: '#FFFFFF' },
  { name: 'Rouge', value: '#FF0000' },
  { name: 'Bleu', value: '#0000FF' },
  { name: 'Vert', value: '#008000' },
  { name: 'Jaune', value: '#FFFF00' },
  { name: 'Gris', value: '#808080' },
  { name: 'Marron', value: '#A52A2A' },
  { name: 'Rose', value: '#FFC0CB' },
  { name: 'Orange', value: '#FFA500' },
  { name: 'Violet', value: '#800080' },
  { name: 'Beige', value: '#F5F5DC' },
  { name: 'Doré', value: '#FFD700' },
  { name: 'Argenté', value: '#C0C0C0' },
  { name: 'Multicolore', value: 'multicolor' },
  { name: 'Autre', value: 'other' },
];

// Countries for delivery
const COUNTRIES = [
  { code: 'HT', name: 'Haïti' },
  { code: 'DO', name: 'République Dominicaine' },
  { code: 'US', name: 'États-Unis' },
  { code: 'CA', name: 'Canada' },
  { code: 'FR', name: 'France' },
  { code: 'JM', name: 'Jamaïque' },
  { code: 'CU', name: 'Cuba' },
  { code: 'PR', name: 'Porto Rico' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BB', name: 'Barbade' },
  { code: 'TT', name: 'Trinité-et-Tobago' },
  { code: 'MX', name: 'Mexique' }
];

const MAX_PRICE = 9999999.99 // Maximum value for decimal(10,2)

export function ProductForm({ onClose, onSuccess, product }: ProductFormProps) {
  const user = useAuthStore((state) => state.user)
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [images, setImages] = useState<File[]>([])
  const [existingImages, setExistingImages] = useState<string[]>([]) // For storing existing image URLs
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    minPrice: '',
    category: '',
    enableNegotiation: false,
    deliveryTypes: {
      standard: true,
      express: false,
      pickup: false
    },
    deliveryCountries: [] as string[],
    pickupAddress: '',
    estimatedDelivery: '3-5',
    sizes: [] as string[], // Add sizes property
    colors: [] as string[], // Changed from single color to colors array
  })
  const [error, setError] = useState('')
  const [availableSizes, setAvailableSizes] = useState<string[]>([]) // State for available sizes based on category

  // Initialize form with product data if editing
  useEffect(() => {
    if (product) {
      setForm({
        title: product.title || '',
        description: product.description || '',
        price: product.price?.toString() || '',
        minPrice: product.min_price?.toString() || '',
        category: product.category || '',
        enableNegotiation: !!product.min_price,
        deliveryTypes: product.delivery_types || {
          standard: true,
          express: false,
          pickup: false
        },
        deliveryCountries: product.delivery_countries || [],
        pickupAddress: product.pickup_address || '',
        estimatedDelivery: product.estimated_delivery || '3-5',
        sizes: product.sizes || [], // Initialize sizes from product data
        colors: product.colors || [], // Use product.colors array instead of single color
      })
      
      // Set available sizes based on category
      updateAvailableSizes(product.category);
      
      // Set existing images if available
      if (product.images && product.images.length > 0) {
        setExistingImages(product.images)
      }
    }
  }, [product])

  const updateAvailableSizes = (category: string) => {
    if (category === 'Fashion') {
      setAvailableSizes(CLOTHING_SIZES);
    } else if (category === 'Sports') {
      setAvailableSizes([...SHOE_SIZES, ...CLOTHING_SIZES]); // Both for sports category
    } else {
      setAvailableSizes([]);
    }
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length + images.length + existingImages.length > 10) {
      setToastMessage('Maximum 10 images allowed')
      setToastType('error')
      setShowToast(true)
      return
    }
    setImages(prev => [...prev, ...files])
  }

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index))
  }

  const removeExistingImage = (index: number) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index))
  }

  const validatePrice = (price: string): boolean => {
    const numPrice = parseFloat(price)
    return !isNaN(numPrice) && numPrice > 0 && numPrice <= MAX_PRICE
  }

  const toggleSize = (size: string) => {
    setForm(prev => {
      const newSizes = [...prev.sizes];
      const index = newSizes.indexOf(size);
      
      if (index >= 0) {
        // Remove size if already selected
        newSizes.splice(index, 1);
      } else {
        // Add size if not selected
        newSizes.push(size);
      }
      
      return {
        ...prev,
        sizes: newSizes
      };
    });
  }

  const toggleColor = (color: string) => {
    setForm(prev => {
      const newColors = [...prev.colors];
      const index = newColors.indexOf(color);
      
      if (index >= 0) {
        // Remove color if already selected
        newColors.splice(index, 1);
      } else {
        // Add color if not selected
        newColors.push(color);
      }
      
      return {
        ...prev,
        colors: newColors
      };
    });
  }

  const toggleDeliveryCountry = (code: string) => {
    setForm(prev => {
      const newCountries = [...prev.deliveryCountries];
      const index = newCountries.indexOf(code);
      
      if (index >= 0) {
        // Remove country if already selected
        newCountries.splice(index, 1);
      } else {
        // Add country if not selected
        newCountries.push(code);
      }
      
      return {
        ...prev,
        deliveryCountries: newCountries
      };
    });
  }

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const category = e.target.value;
    setForm(prev => ({ ...prev, category }));
    updateAvailableSizes(category);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate minimum images
    if (images.length + existingImages.length < 1) {
      setError('Au moins une image est requise')
      setToastMessage('Au moins une image est requise')
      setToastType('error')
      setShowToast(true)
      return
    }

    // Validate price
    if (!validatePrice(form.price)) {
      setError(`Le prix doit être entre 0 et ${MAX_PRICE}`)
      setToastMessage(`Le prix doit être entre 0 et ${MAX_PRICE}`)
      setToastType('error')
      setShowToast(true)
      return
    }

    // Validate min price if negotiation is enabled
    if (form.enableNegotiation && !validatePrice(form.minPrice)) {
      setError(`Le prix minimum doit être entre 0 et ${MAX_PRICE}`)
      setToastMessage(`Le prix minimum doit être entre 0 et ${MAX_PRICE}`)
      setToastType('error')
      setShowToast(true)
      return
    }

    if (!user) {
      setError('Vous devez être connecté pour ajouter un produit')
      setToastMessage('Vous devez être connecté pour ajouter un produit')
      setToastType('error')
      setShowToast(true)
      return
    }

    setLoading(true)
    try {
      // Check if seller has reached product limit (only for new products)
      if (!product) {
        const { data: storeData, error: storeError } = await supabase
          .from('seller_stores')
          .select('subscription_type, monthly_product_count')
          .eq('seller_id', user.id)
          .single()

        if (storeError) {
          console.error('Error fetching seller store:', storeError)
          throw new Error('Erreur lors de la vérification de votre boutique')
        }

        const productLimit = storeData.subscription_type === 'premium' ? 25 : 8
        const currentCount = storeData.monthly_product_count || 0

        if (currentCount >= productLimit) {
          setError(`Vous avez atteint la limite de ${productLimit} produits pour votre abonnement ${storeData.subscription_type}`)
          setToastMessage(`Vous avez atteint la limite de ${productLimit} produits pour votre abonnement ${storeData.subscription_type}`)
          setToastType('error')
          setShowToast(true)
          setLoading(false)
          return
        }
      }

      // Process images - keep existing ones and upload new ones
      let imageUrls = [...existingImages]
      
      // Upload new images
      if (images.length > 0) {
        try {
          const newImageUrls = await Promise.all(
            images.map(async (file) => {
              const fileExt = file.name.split('.').pop()
              const fileName = `${Math.random()}.${fileExt}`
              const filePath = `${user.id}/${fileName}`

              const { error: uploadError } = await supabase.storage
                .from('product-images')
                .upload(filePath, file)

              if (uploadError) {
                console.error('Error uploading image:', uploadError)
                throw new Error('Erreur lors du téléchargement des images')
              }

              const { data: { publicUrl } } = supabase.storage
                .from('product-images')
                .getPublicUrl(filePath)

              return publicUrl
            })
          )
          
          imageUrls = [...imageUrls, ...newImageUrls]
        } catch (err) {
          console.error('Error uploading images:', err)
          throw new Error('Erreur lors du téléchargement des images')
        }
      }

      // Prepare the product data
      const productData: any = {
        title: form.title,
        description: form.description,
        price: parseFloat(form.price),
        min_price: form.enableNegotiation ? parseFloat(form.minPrice) : null,
        category: form.category,
        delivery_types: form.deliveryTypes,
        delivery_countries: form.deliveryCountries,
        pickup_address: form.deliveryTypes.pickup ? form.pickupAddress : null,
        estimated_delivery: form.estimatedDelivery,
        images: imageUrls,
        sizes: form.sizes,
        colors: form.colors // Use colors array
      }

      if (product) {
        // Update existing product
        const { error: productError } = await supabase
          .from('products')
          .update(productData)
          .eq('id', product.id)
          .eq('seller_id', user.id)

        if (productError) {
          console.error('Error updating product:', productError)
          throw new Error('Erreur lors de la mise à jour du produit')
        }

        setToastMessage('Produit mis à jour avec succès')
      } else {
        // Create new product with pending status
        const { error: productError } = await supabase
          .from('products')
          .insert([{
            seller_id: user.id,
            status: 'pending', // Set initial status as pending for admin approval
            ...productData
          }])

        if (productError) {
          console.error('Error creating product:', productError)
          throw new Error('Erreur lors de la création du produit')
        }

        // Update monthly product count
        const { data: storeData } = await supabase
          .from('seller_stores')
          .select('monthly_product_count')
          .eq('seller_id', user.id)
          .single()
          
        const currentCount = storeData?.monthly_product_count || 0

        const { error: updateError } = await supabase
          .from('seller_stores')
          .update({ 
            monthly_product_count: currentCount + 1 
          })
          .eq('seller_id', user.id)

        if (updateError) {
          console.error('Error updating product count:', updateError)
          // Non-critical error, don't throw
        }

        // Create notification for admin
        const { error: notificationError } = await supabase
          .from('notifications')
          .insert([
            {
              title: 'Nouveau produit en attente',
              content: `Un nouveau produit "${form.title}" attend votre approbation`,
              type: 'system',
              metadata: {
                seller_id: user.id,
                seller_name: user.full_name || user.email,
                product_title: form.title
              }
            }
          ])

        if (notificationError) {
          console.error('Error creating notification:', notificationError)
          // Non-critical error, don't throw
        }

        setToastMessage('Produit soumis pour approbation')
      }
      
      setToastType('success')
      setShowToast(true)
      setTimeout(() => {
        onSuccess()
        onClose()
      }, 2000)
    } catch (err) {
      console.error('Error creating/updating product:', err)
      setError(err instanceof Error ? err.message : 'Erreur lors de la modification du produit')
      setToastMessage(err instanceof Error ? err.message : 'Erreur lors de la modification du produit')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
          position="center"
        />
      )}

      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-3 sm:p-6">
          <div className="flex justify-between items-start border-b border-gray-200 pb-3 sm:pb-4">
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">
                {product ? 'Modifier le Produit' : 'Nouveau Produit'}
              </h2>
              <div className="mt-2 flex items-center text-xs sm:text-sm text-yellow-600 bg-yellow-50 rounded-lg p-2">
                <Info className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 flex-shrink-0" />
                <span className="flex-1">
                  {product 
                    ? 'Vos modifications seront enregistrées immédiatement'
                    : 'Votre produit sera examiné par notre équipe avant d\'être publié'
                  }
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="mt-4 sm:mt-6 space-y-4 sm:space-y-8">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-3 sm:p-6 border border-gray-200">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex items-center">
                <Tag className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                Informations Produit
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                    Titre du produit *
                  </label>
                  <input
                    type="text"
                    id="title"
                    required
                    maxLength={100}
                    value={form.title}
                    onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                    className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-primary text-sm px-3 py-2 sm:px-4 sm:py-2"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    required
                    minLength={10}
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-primary text-sm px-3 py-2 sm:px-4 sm:py-2"
                  />
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                    Catégorie *
                  </label>
                  <select
                    id="category"
                    required
                    value={form.category}
                    onChange={handleCategoryChange}
                    className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-primary text-sm px-3 py-2 sm:px-4 sm:py-2"
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {CATEGORIES.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Color Section - Available for all categories */}
            <div className="bg-gray-50 rounded-lg p-3 sm:p-6 border border-gray-200">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex items-center">
                <Tag className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                Couleur
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sélectionnez les couleurs disponibles
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {COMMON_COLORS.map(color => (
                    <div 
                      key={color.value}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                        form.colors.includes(color.value) 
                          ? 'border-primary bg-primary/5' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => toggleColor(color.value)}
                    >
                      <div 
                        className="w-6 h-6 rounded-full border border-gray-300 mr-3 flex-shrink-0"
                        style={{ 
                          backgroundColor: color.value === 'multicolor' 
                            ? 'linear-gradient(to right, red, orange, yellow, green, blue, indigo, violet)' 
                            : color.value === 'other' ? '#ccc' : color.value 
                        }}
                      >
                        {form.colors.includes(color.value) && (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-2 h-2 bg-primary rounded-full"></div>
                          </div>
                        )}
                      </div>
                      <span className="text-sm">{color.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          
            {/* Sizes Information */}
            {availableSizes.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-3 sm:p-6 border border-gray-200">
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex items-center">
                  <Tag className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                  Tailles Disponibles
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  <p className="text-sm text-gray-500">
                    Sélectionnez toutes les tailles disponibles pour ce produit
                  </p>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {availableSizes.map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => toggleSize(size)}
                        className={`py-2 px-3 border ${
                          form.sizes.includes(size)
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-gray-300 hover:border-gray-400 text-gray-700'
                        } rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Delivery Countries Selection */}
            <div className="bg-gray-50 rounded-lg p-3 sm:p-6 border border-gray-200">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex items-center">
                <Globe className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                Pays de Livraison Disponibles
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-sm text-gray-500">
                  Sélectionnez tous les pays où ce produit peut être livré
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {COUNTRIES.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => toggleDeliveryCountry(country.code)}
                      className={`py-2 px-3 border ${
                        form.deliveryCountries.includes(country.code)
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-gray-300 hover:border-gray-400 text-gray-700'
                      } rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary flex justify-between items-center`}
                    >
                      <span>{country.name}</span>
                      <span className="text-xs font-bold">{country.code}</span>
                    </button>
                  ))}
                </div>
                {form.deliveryCountries.length === 0 && (
                  <p className="text-xs text-red-500">
                    Veuillez sélectionner au moins un pays pour la livraison
                  </p>
                )}
              </div>
            </div>

            {/* Price and Negotiation */}
            <div className="bg-gray-50 rounded-lg p-3 sm:p-6 border border-gray-200">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex items-center">
                <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                Prix et Négociation
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                    Prix *
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="price"
                      required
                      min="0.01"
                      max={MAX_PRICE}
                      step="0.01"
                      value={form.price}
                      onChange={(e) => setForm(prev => ({ ...prev, price: e.target.value }))}
                      className="block w-full pl-8 sm:pl-10 pr-8 sm:pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm py-2"
                    />
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableNegotiation"
                      checked={form.enableNegotiation}
                      onChange={(e) => setForm(prev => ({ ...prev, enableNegotiation: e.target.checked }))}
                      className="h-4 w-4 text-primary focus:ring-2 focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="enableNegotiation" className="ml-2 block text-sm text-gray-900">
                      Activer la négociation de prix
                    </label>
                  </div>

                  {form.enableNegotiation && (
                    <div className="mt-3 sm:mt-4">
                      <label htmlFor="minPrice" className="block text-sm font-medium text-gray-700">
                        Prix minimum *
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                        </div>
                        <input
                          type="number"
                          id="minPrice"
                          required
                          min="0.01"
                          max={MAX_PRICE}
                          step="0.01"
                          value={form.minPrice}
                          onChange={(e) => setForm(prev => ({ ...prev, minPrice: e.target.value }))}
                          className="block w-full pl-8 sm:pl-10 pr-8 sm:pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm py-2"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Delivery Options */}
            <div className="bg-gray-50 rounded-lg p-3 sm:p-6 border border-gray-200">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex items-center">
                <Truck className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                Options de Livraison
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="flex items-center h-full">
                      <input
                        type="checkbox"
                        id="standard"
                        checked={form.deliveryTypes.standard}
                        onChange={(e) => setForm(prev => ({
                          ...prev,
                          deliveryTypes: {
                            ...prev.deliveryTypes,
                            standard: e.target.checked
                          }
                        }))}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label htmlFor="standard" className="ml-3 block text-sm font-medium text-gray-700">
                        Livraison standard
                      </label>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center h-full">
                      <input
                        type="checkbox"
                        id="express"
                        checked={form.deliveryTypes.express}
                        onChange={(e) => setForm(prev => ({
                          ...prev,
                          deliveryTypes: {
                            ...prev.deliveryTypes,
                            express: e.target.checked
                          }
                        }))}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label htmlFor="express" className="ml-3 block text-sm font-medium text-gray-700">
                        Livraison express
                      </label>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center h-full">
                      <input
                        type="checkbox"
                        id="pickup"
                        checked={form.deliveryTypes.pickup}
                        onChange={(e) => setForm(prev => ({
                          ...prev,
                          deliveryTypes: {
                            ...prev.deliveryTypes,
                            pickup: e.target.checked
                          }
                        }))}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label htmlFor="pickup" className="ml-3 block text-sm font-medium text-gray-700">
                        Retrait en personne
                      </label>
                    </div>
                  </div>
                </div>

                {form.deliveryTypes.pickup && (
                  <div>
                    <label htmlFor="pickupAddress" className="block text-sm font-medium text-gray-700">
                      Adresse de retrait
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MapPin className="h-5 w-5 text-gray-400" />
                      </div>
                      <textarea
                        id="pickupAddress"
                        required={form.deliveryTypes.pickup}
                        value={form.pickupAddress}
                        onChange={(e) => setForm(prev => ({ ...prev, pickupAddress: e.target.value }))}
                        rows={2}
                        className="block w-full pl-10 border border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary sm:text-sm px-4 py-2"
                        placeholder="Entrez l'adresse complète où les clients peuvent récupérer ce produit"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="estimatedDelivery" className="block text-sm font-medium text-gray-700">
                    Délai de livraison estimé (jours)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Clock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="estimatedDelivery"
                      value={form.estimatedDelivery}
                      onChange={(e) => setForm(prev => ({ ...prev, estimatedDelivery: e.target.value }))}
                      className="block w-full pl-10 border border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary sm:text-sm px-4 py-2"
                      placeholder="3-5"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="bg-gray-50 rounded-lg p-3 sm:p-6 border border-gray-200">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex items-center">
                <ImageIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                Images du Produit
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center">
                  <label htmlFor="images" className="block text-sm font-medium text-gray-700">
                    Images du produit (max. 10)
                  </label>
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {existingImages.length + images.length}/10
                  </span>
                </div>

                {/* Existing Images */}
                {existingImages.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Images existantes</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {existingImages.map((url, index) => (
                        <div key={index} className="relative">
                          <img src={url} alt={`Image ${index + 1}`} className="h-24 w-full object-cover rounded-lg"/>
                          <button
                            type="button"
                            onClick={() => removeExistingImage(index)}
                            className="absolute top-2 right-2 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Images */}
                {images.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Nouvelles images</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {images.map((file, index) => (
                        <div key={index} className="relative">
                          <img 
                            src={URL.createObjectURL(file)} 
                            alt={`Preview ${index + 1}`} 
                            className="h-24 w-full object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-2 right-2 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* File Input */}
                <div className="mt-1">
                  <label
                    htmlFor="images"
                    className="block w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-center cursor-pointer hover:border-primary transition-colors"
                  >
                    <Upload className="h-6 w-6 mx-auto text-gray-400" />
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      Ajouter des images
                    </span>
                    <input
                      id="images"
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageChange}
                      className="sr-only"
                      disabled={images.length + existingImages.length >= 10}
                    />
                  </label>
                </div>
              </div>
              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : null}
                {product ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}